/**
 * Generic Wizard Footer
 * Fixed navigation footer with Previous/Next buttons
 * Extracted from KYCWizard lines 198-237 and generified
 */

import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useWizardContext } from './WizardContext';

const WizardFooter: React.FC = () => {
  const {
    config,
    currentStep,
    totalSteps,
    reviewMode,
    goToPreviousStep,
    submitForm,
    isSaving
  } = useWizardContext();

  const { theme, formId } = config;

  return (
    <div className="border-t border-gray-200 bg-white shadow-lg">
      <div className="max-w-4xl mx-auto px-8 py-4 flex items-center justify-between">
        {/* Previous button */}
        <div>
          {currentStep > 1 && !(reviewMode && currentStep === 2) && (
            <button
              onClick={goToPreviousStep}
              type="button"
              className={`px-6 py-2.5 rounded-lg font-medium flex items-center gap-2
                ${theme.secondaryButton.bg} ${theme.secondaryButton.text} ${theme.secondaryButton.hover}
                ${theme.secondaryButton.border || ''}
                focus:outline-none focus:ring-2 ${theme.primaryButton.focus || 'focus:ring-kielis-gold'}
                transition-colors`}
            >
              <ChevronLeft className="w-4 h-4" />
              Previous
            </button>
          )}
        </div>

        {/* Step indicator */}
        <div className="text-sm text-gray-500">
          Step {currentStep} of {totalSteps}
        </div>

        {/* Next/Submit button */}
        <div>
          {currentStep < totalSteps ? (
            <button
              type="submit"
              form={`${formId}-step-${currentStep}-form`}
              onClick={(_e) => {
                // Prevent form submission, let the form handle it
                // This button triggers the form's onSubmit
              }}
              className={`px-6 py-2.5 rounded-lg font-medium flex items-center gap-2
                ${theme.primaryButton.bg} ${theme.primaryButton.text} ${theme.primaryButton.hover}
                ${theme.primaryButton.border || ''}
                focus:outline-none focus:ring-2 ${theme.primaryButton.focus || 'focus:ring-kielis-gold'}
                transition-colors`}
            >
              {reviewMode ? 'Mark as Reviewed & Continue' : 'Save & Continue'}
              <ChevronRight className="w-4 h-4" />
            </button>
          ) : (
            <button
              type="button"
              onClick={submitForm}
              disabled={isSaving}
              className={`px-6 py-2.5 rounded-lg font-medium flex items-center gap-2
                ${theme.primaryButton.bg} ${theme.primaryButton.text} ${theme.primaryButton.hover}
                ${theme.primaryButton.border || ''}
                focus:outline-none focus:ring-2 ${theme.primaryButton.focus || 'focus:ring-kielis-gold'}
                transition-colors disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {isSaving ? 'Submitting...' : `Submit ${config.formTitle}`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default WizardFooter;
